import { useMediaQuery } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../../../global-state/themeContext';
import { StoreSalesPerformanceResponse, useStoreInformation } from '../../../../../hooks';
import { Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { HeaderRowItem, Table } from '../../../../../stories/molecules/tablev2/Table';
import { muiTheme } from '../../../../../theme';
import { storeOverviewTranslations } from '../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../utils';
import { StoreWidgetContent } from '../../../../storeOverviewPage/storeWidgetContent/StoreWidgetContent';
import { ToggleChipGroup } from '../../../common/toggleChipGroup/ToggleChipGroup';
import { HeaderTooltip } from '../../headerTooltip/HeaderTooltip';
import { LayoutDetails, MobileChipLayout } from '../mobileChipLayout/MobileChipLayout';
import { StyledIndexTypography } from '../mobileChipLayout/MobileChipLayout.styles';
import { chipDataMapping, ChipKey, ChipOption, getIndexStatus, useChipOptionsMap } from '../sales.util';
import { EquationItem, EquationTimePeriod, getEquationData, getMetricFormatSpec } from './util';

enum EquationColumnType {
  METRIC = 'metric',
  LCDNUMBERS = 'lcdNumbers',
  LCDLY = 'lcdLy',
  LCDVSAREA = 'lcdVsArea',
  LCDVSSM = 'lcdVsSM',
  CURRWEEKNUMBERS = 'currWeekNumbers',
  CURRWEEKLY = 'currWeekLy',
  CURRWEEKVSAREA = 'currWeekVsArea',
  CURRWEEKVSSM = 'currWeekVsSM',
  LCWNUMBERS = 'lcwNumbers',
  LCWLY = 'lcwLy',
  LCWVSAREA = 'lcwVsArea',
  LCWVSSM = 'lcwVsSM',
  L4NUMBERS = 'l4Numbers',
  L4LY = 'l4Ly',
  L4VSAREA = 'l4VsArea',
  L4VSSM = 'l4VsSM',
}
type EquationDataGroupedByTime = Record<EquationTimePeriod, EquationItem>;
type EquationMetricType = 'traffic' | 'conversion' | 'ppr' | 'pricePerPiece' | 'avgPurchase' | 'revVisitor';

export type EquationWidgetProps = {
  isLoading: boolean;
  isError: boolean;
  selectedFilter: string | undefined;
  salesPerformanceData: StoreSalesPerformanceResponse | null;
  retry: () => void;
};

export const EquationWidget: FC<EquationWidgetProps> = ({
  isLoading,
  isError,
  selectedFilter,
  salesPerformanceData,
  retry,
}) => {
  const { equation: equationTransations, headerTooltips: headerTooltipsTranslations } = storeOverviewTranslations;

  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const { chipOptionsMap } = useChipOptionsMap();
  const { corporateBrandId } = useStoreInformation();
  const isHMBrand = corporateBrandId === 0;

  const hmBrandColumnsBorder = `:nth-of-type(5), :nth-of-type(9)`;
  const otherBrandColumnsBorder = `:nth-of-type(4), :nth-of-type(7)`;
  const brandsBorderSelector = isHMBrand ? hmBrandColumnsBorder : otherBrandColumnsBorder;

  const isDarkMode = mode === 'dark';
  const themeBorderColor = isDarkMode ? muiTheme.color.borderGrayDark : muiTheme.color.borderGray;
  const [selectedMobileChip, setSelectedMobileChip] = useState<ChipKey>('LCD');
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const [showLCD, setShowLCD] = useState(true);

  const equationData = useMemo(
    () => (salesPerformanceData ? getEquationData(salesPerformanceData, selectedFilter) : null),
    [salesPerformanceData, selectedFilter],
  );

  const getTableRow = (item: EquationDataGroupedByTime, metricName: string) => {
    const { unit, decimals } = getMetricFormatSpec(metricName);

    const showDash = !!(selectedFilter && metricName !== 'Price/Pcs');

    return [
      {
        id: showLCD ? EquationColumnType.LCDNUMBERS : EquationColumnType.CURRWEEKNUMBERS,
        content: (
          <>
            {showDash && <Typography>-</Typography>}
            {!showDash && (
              <Typography>
                {showLCD
                  ? formatAmountWithSeparator(item.currentDay.numbers, unit, decimals)
                  : formatAmountWithSeparator(item.currentWeek.numbers, unit, decimals)}
              </Typography>
            )}
          </>
        ),
      },
      {
        id: showLCD ? EquationColumnType.LCDLY : EquationColumnType.CURRWEEKLY,
        content: (
          <>
            {showDash && <Typography>-</Typography>}
            {!showDash && (
              <StyledIndexTypography
                status={getIndexStatus(showLCD ? item.currentDay.vsLy : item.currentWeek.vsLy)}
              >
                {showLCD ? item.currentDay.vsLy : item.currentWeek.vsLy}
              </StyledIndexTypography>
            )}
          </>
        ),
      },
      {
        id: showLCD ? EquationColumnType.LCDVSAREA : EquationColumnType.CURRWEEKVSAREA,
        content: (
          <>
            {showDash && <Typography>-</Typography>}
            {!showDash && (
              <StyledIndexTypography
                status={getIndexStatus(showLCD ? item.currentDay.vsArea : item.currentWeek.vsArea)}
              >
                {showLCD ? item.currentDay.vsArea : item.currentWeek.vsArea}
              </StyledIndexTypography>
            )}
          </>
        ),
      },
      ...(isHMBrand
        ? [
            {
              id: showLCD ? EquationColumnType.LCDVSSM : EquationColumnType.CURRWEEKVSSM,
              content: (
                <>
                  {showDash && <Typography>-</Typography>}
                  {!showDash && (
                    <StyledIndexTypography
                      status={getIndexStatus(showLCD ? item.currentDay.vsSM : item.currentWeek.vsSM)}
                    >
                      {showLCD ? item.currentDay.vsSM : item.currentWeek.vsSM}
                    </StyledIndexTypography>
                  )}
                </>
              ),
            },
          ]
        : []),
      {
        id: EquationColumnType.LCWNUMBERS,
        content: (
          <>
            {showDash && <Typography>-</Typography>}
            {!showDash && (
              <Typography>{formatAmountWithSeparator(item.lastCompleteWeek.numbers, unit, decimals)}</Typography>
            )}
          </>
        ),
      },
      {
        id: EquationColumnType.LCWLY,
        content: (
          <>
            {showDash && <Typography>-</Typography>}
            {!showDash && (
              <StyledIndexTypography status={getIndexStatus(item.lastCompleteWeek.vsLy)}>
                {item.lastCompleteWeek.vsLy}
              </StyledIndexTypography>
            )}
          </>
        ),
      },
      {
        id: EquationColumnType.LCWVSAREA,
        content: (
          <>
            {showDash && <Typography>-</Typography>}
            {!showDash && (
              <StyledIndexTypography status={getIndexStatus(item.lastCompleteWeek.vsArea)}>
                {item.lastCompleteWeek.vsArea}
              </StyledIndexTypography>
            )}
          </>
        ),
      },
      ...(isHMBrand
        ? [
            {
              id: EquationColumnType.LCWVSSM,
              content: (
                <>
                  {showDash && <Typography>-</Typography>}
                  {!showDash && (
                    <StyledIndexTypography status={getIndexStatus(item.lastCompleteWeek.vsSM)}>
                      {item.lastCompleteWeek.vsSM}
                    </StyledIndexTypography>
                  )}
                </>
              ),
            },
          ]
        : []),
      {
        id: EquationColumnType.L4NUMBERS,
        content: (
          <>
            {showDash && <Typography>-</Typography>}
            {!showDash && (
              <Typography>{formatAmountWithSeparator(item.lastFourWeeks.numbers, unit, decimals)}</Typography>
            )}
          </>
        ),
      },
      {
        id: EquationColumnType.L4LY,
        content: (
          <>
            {showDash && <Typography>-</Typography>}
            {!showDash && (
              <StyledIndexTypography status={getIndexStatus(item.lastFourWeeks.vsLy)}>
                {item.lastFourWeeks.vsLy}
              </StyledIndexTypography>
            )}
          </>
        ),
      },
      {
        id: EquationColumnType.L4VSAREA,
        content: (
          <>
            {showDash && <Typography>-</Typography>}
            {!showDash && (
              <StyledIndexTypography status={getIndexStatus(item.lastFourWeeks.vsArea)}>
                {item.lastFourWeeks.vsArea}
              </StyledIndexTypography>
            )}
          </>
        ),
      },
      ...(isHMBrand
        ? [
            {
              id: EquationColumnType.L4VSSM,
              content: (
                <>
                  {showDash && <Typography>-</Typography>}
                  {!showDash && (
                    <StyledIndexTypography status={getIndexStatus(item.lastFourWeeks.vsSM)}>
                      {item.lastFourWeeks.vsSM}
                    </StyledIndexTypography>
                  )}
                </>
              ),
            },
          ]
        : []),
    ];
  };

  const getEquationDetailsForMobile = (key: EquationMetricType, period: EquationTimePeriod): LayoutDetails[] => {
    if (!equationData || !equationData[period] || !equationData[period]?.[key]) {
      return [];
    }
    const { unit, decimals } = getMetricFormatSpec(key);
    const showDash = !!(selectedFilter && key !== 'pricePerPiece');

    const salesMarketItem: LayoutDetails[] = [
      {
        label: 'vs SM',
        value: showDash ? '-' : (equationData[period]?.[key].vsSM ?? 0).toString(),
        status: showDash ? 'default' : getIndexStatus(equationData[period]?.[key].vsSM ?? 0),
      },
    ];

    return [
      {
        label: 'Numbers',
        value: showDash
          ? '-'
          : formatAmountWithSeparator(equationData[period]?.[key].numbers ?? 0, unit, decimals),
        status: 'default',
      },
      {
        label: 'vs LY',
        value: showDash ? '-' : (equationData[period]?.[key].vsLy ?? 0).toString(),
        status: showDash ? 'default' : getIndexStatus(equationData[period]?.[key].vsLy ?? 0),
      },
      {
        label: 'vs Area',
        value: showDash ? '-' : (equationData[period]?.[key].vsArea ?? 0).toString(),
        status: showDash ? 'default' : getIndexStatus(equationData[period]?.[key].vsArea ?? 0),
      },
      ...(isHMBrand ? salesMarketItem : []),
    ];
  };

  const mobileChipOptions: ChipOption[] = [
    chipOptionsMap.LCD,
    chipOptionsMap.CW,
    chipOptionsMap.LCW,
    chipOptionsMap.L4Wks,
  ];

  const desktopChipOptions: ChipOption[] = [chipOptionsMap.LCD, chipOptionsMap.CW];

  const headerItems: HeaderRowItem<EquationColumnType>[] = [
    {
      id: EquationColumnType.METRIC,
      content: (
        <Typography type="body3">
          {t(equationTransations.metric.key, equationTransations.metric.defaultValue)}
        </Typography>
      ),
    },
    {
      id: showLCD ? EquationColumnType.LCDNUMBERS : EquationColumnType.CURRWEEKNUMBERS,
      content: (
        <ColumnFlexContainer>
          <Typography type="body3">{showLCD ? 'Selected day' : 'CW'}</Typography>
          <Typography type="body3">
            {t(storeOverviewTranslations.numbers.key, storeOverviewTranslations.numbers.defaultValue)}
          </Typography>
        </ColumnFlexContainer>
      ),
    },
    {
      id: showLCD ? EquationColumnType.LCDLY : EquationColumnType.CURRWEEKLY,
      content: (
        <HeaderTooltip
          headerText="vs LY"
          tooltipText={t(headerTooltipsTranslations.vsLY.key, headerTooltipsTranslations.vsLY.defaultValue)}
        />
      ),
    },
    {
      id: showLCD ? EquationColumnType.LCDVSAREA : EquationColumnType.CURRWEEKVSAREA,
      content: (
        <HeaderTooltip
          headerText="vs Area"
          tooltipText={t(headerTooltipsTranslations.vsArea.key, headerTooltipsTranslations.vsArea.defaultValue)}
        />
      ),
    },
    ...(isHMBrand
      ? [
          {
            id: showLCD ? EquationColumnType.LCDVSSM : EquationColumnType.CURRWEEKVSSM,
            content: (
              <HeaderTooltip
                headerText="vs SM"
                tooltipText={t(headerTooltipsTranslations.vsSM.key, headerTooltipsTranslations.vsSM.defaultValue)}
              />
            ),
          },
        ]
      : []),
    {
      id: EquationColumnType.LCWNUMBERS,
      content: (
        <ColumnFlexContainer>
          <Typography type="body3">LCW</Typography>
          <Typography type="body3">
            {t(storeOverviewTranslations.numbers.key, storeOverviewTranslations.numbers.defaultValue)}
          </Typography>
        </ColumnFlexContainer>
      ),
    },
    {
      id: EquationColumnType.LCWLY,
      content: (
        <HeaderTooltip
          headerText="vs LY"
          tooltipText={t(headerTooltipsTranslations.vsLY.key, headerTooltipsTranslations.vsLY.defaultValue)}
        />
      ),
    },
    {
      id: EquationColumnType.LCWVSAREA,
      content: (
        <HeaderTooltip
          headerText="vs Area"
          tooltipText={t(headerTooltipsTranslations.vsArea.key, headerTooltipsTranslations.vsArea.defaultValue)}
        />
      ),
    },
    ...(isHMBrand
      ? [
          {
            id: EquationColumnType.LCWVSSM,
            content: (
              <HeaderTooltip
                headerText="vs SM"
                tooltipText={t(headerTooltipsTranslations.vsSM.key, headerTooltipsTranslations.vsSM.defaultValue)}
              />
            ),
          },
        ]
      : []),
    {
      id: EquationColumnType.L4NUMBERS,
      content: (
        <ColumnFlexContainer>
          <Typography type="body3">L4Wks</Typography>
          <Typography type="body3">
            {t(storeOverviewTranslations.numbers.key, storeOverviewTranslations.numbers.defaultValue)}
          </Typography>
        </ColumnFlexContainer>
      ),
    },
    {
      id: EquationColumnType.L4LY,
      content: (
        <HeaderTooltip
          headerText="vs LY"
          tooltipText={t(headerTooltipsTranslations.vsLY.key, headerTooltipsTranslations.vsLY.defaultValue)}
        />
      ),
    },
    {
      id: EquationColumnType.L4VSAREA,
      content: (
        <HeaderTooltip
          headerText="vs Area"
          tooltipText={t(headerTooltipsTranslations.vsArea.key, headerTooltipsTranslations.vsArea.defaultValue)}
        />
      ),
    },
    ...(isHMBrand
      ? [
          {
            id: EquationColumnType.L4VSSM,
            content: (
              <HeaderTooltip
                headerText="vs SM"
                tooltipText={t(headerTooltipsTranslations.vsSM.key, headerTooltipsTranslations.vsSM.defaultValue)}
              />
            ),
          },
        ]
      : []),
  ];

  const tableItems = useMemo(() => {
    const EQUATIONMETRICS = [
      'traffic',
      'conversion',
      'ppr',
      'pricePerPiece',
      'avgPurchase',
      'revVisitor',
    ] as const;

    const metricObject: EquationItem = {
      numbers: 0,
      vsLy: 0,
      vsArea: 0,
      vsSM: 0,
    };

    const periodObject = {
      currentDay: metricObject,
      currentWeek: metricObject,
      lastCompleteWeek: metricObject,
      lastFourWeeks: metricObject,
    };

    const result = {
      traffic: periodObject,
      conversion: periodObject,
      ppr: periodObject,
      pricePerPiece: periodObject,
      avgPurchase: periodObject,
      revVisitor: periodObject,
    };

    if (!equationData) {
      return [];
    }

    const periods = Object.keys(equationData) as Array<EquationTimePeriod>;

    EQUATIONMETRICS.forEach((metric) => {
      const equationDataByPeriod = {} as Record<EquationTimePeriod, EquationItem>;

      periods.forEach((period) => {
        const item = equationData[period]?.[metric];
        equationDataByPeriod[period] = item ?? metricObject;
      });

      result[metric] = equationDataByPeriod;
    });

    return [
      {
        metricName: t(equationTransations.traffic.key, equationTransations.traffic.defaultValue),
        data: result.traffic,
        tooltip: t(headerTooltipsTranslations.traffic.key, headerTooltipsTranslations.traffic.defaultValue),
      },
      {
        metricName: t(equationTransations.conversion.key, equationTransations.conversion.defaultValue),
        data: result.conversion,
        tooltip: t(headerTooltipsTranslations.conversion.key, headerTooltipsTranslations.conversion.defaultValue),
      },
      {
        metricName: 'PPR',
        data: result.ppr,
        tooltip: t(headerTooltipsTranslations.ppr.key, headerTooltipsTranslations.ppr.defaultValue),
      },
      {
        metricName: t(equationTransations.pricePerPiece.key, equationTransations.pricePerPiece.defaultValue),
        data: result.pricePerPiece,
        tooltip: t(
          headerTooltipsTranslations.pricePerPiece.key,
          headerTooltipsTranslations.pricePerPiece.defaultValue,
        ),
      },
      {
        metricName: t(equationTransations.avgPurchase.key, equationTransations.avgPurchase.defaultValue),
        data: result.avgPurchase,
        tooltip: t(
          headerTooltipsTranslations.avgPurchase.key,
          headerTooltipsTranslations.avgPurchase.defaultValue,
        ),
      },
      {
        metricName: t(equationTransations.visitor.key, equationTransations.visitor.defaultValue),
        data: result.revVisitor,
        tooltip: t(headerTooltipsTranslations.visitor.key, headerTooltipsTranslations.visitor.defaultValue),
      },
    ];
  }, [equationData]);

  return (
    <StoreWidgetContent
      widgetVariant="default"
      title={t(equationTransations.title.key, equationTransations.title.defaultValue)}
      tooltipText={t(equationTransations.tooltip.key, equationTransations.tooltip.defaultValue)}
      isLoading={isLoading}
      isError={isError}
      errorRetryHandler={retry}
      isNoData={!equationData}
      errorText={t(equationTransations.errorText.key, equationTransations.errorText.defaultValue)}
      widgetContent={
        <>
          {!isMobile && (
            <ColumnFlexContainer>
              <ToggleChipGroup
                chipOptions={desktopChipOptions}
                selectedChip={showLCD ? 'LCD' : 'CW'}
                setSelectedChip={(chip) => {
                  setShowLCD(chip === 'LCD');
                }}
              />
              <ColumnFlexContainer
                padding={[0, 0, 2]}
                flex={1}
                minHeight={[0]}
                maxHeight={[70]}
                data-testid="equation-table-wrapper"
              >
                <Table
                  data-testid="equation-table"
                  items={tableItems}
                  headerItems={headerItems}
                  stickyHeader
                  stickyFirstColumn
                  sxProps={{
                    padding: '8px 8px',
                    [brandsBorderSelector]: {
                      border: `1px solid ${themeBorderColor}`,
                      borderLeft: 'none',
                      borderTop: 'none',
                    },
                  }}
                  headerSxProps={{
                    padding: '4px 8px',
                    [brandsBorderSelector]: {
                      border: `1px solid ${themeBorderColor}`,
                      borderLeft: 'none',
                      borderTop: 'none',
                      borderBottom: `1px solid ${themeBorderColor}`,
                    },
                  }}
                  firstCellMinWidth="100px"
                  getRowItems={(item) => {
                    const row = getTableRow(item.data, item.metricName);
                    return [
                      {
                        id: EquationColumnType.METRIC,
                        content: <HeaderTooltip headerText={item.metricName} tooltipText={item.tooltip} />,
                      },
                      ...row,
                    ];
                  }}
                />
              </ColumnFlexContainer>
            </ColumnFlexContainer>
          )}
          {isMobile && (
            <ColumnFlexContainer>
              <ToggleChipGroup
                chipOptions={mobileChipOptions}
                selectedChip={selectedMobileChip}
                setSelectedChip={(chip) => {
                  setSelectedMobileChip(chip);
                }}
              />
              <ColumnFlexContainer overflow="auto" maxHeight="260px" gap={[3]} data-testid="equation-list-wrapper">
                <>
                  <MobileChipLayout
                    layout="row"
                    layoutTitle={t(equationTransations.traffic.key, equationTransations.traffic.defaultValue)}
                    layoutData={getEquationDetailsForMobile(
                      'traffic',
                      chipDataMapping[selectedMobileChip] as EquationTimePeriod,
                    )}
                  />
                  <MobileChipLayout
                    layout="row"
                    layoutTitle={t(
                      equationTransations.conversion.key,
                      equationTransations.conversion.defaultValue,
                    )}
                    layoutData={getEquationDetailsForMobile(
                      'conversion',
                      chipDataMapping[selectedMobileChip] as EquationTimePeriod,
                    )}
                  />
                  <MobileChipLayout
                    layout="row"
                    layoutTitle="PPR"
                    layoutData={getEquationDetailsForMobile(
                      'ppr',
                      chipDataMapping[selectedMobileChip] as EquationTimePeriod,
                    )}
                  />
                  <MobileChipLayout
                    layout="row"
                    layoutTitle={t(
                      equationTransations.pricePerPiece.key,
                      equationTransations.pricePerPiece.defaultValue,
                    )}
                    layoutData={getEquationDetailsForMobile(
                      'pricePerPiece',
                      chipDataMapping[selectedMobileChip] as EquationTimePeriod,
                    )}
                  />
                  <MobileChipLayout
                    layout="row"
                    layoutTitle={t(
                      equationTransations.avgPurchase.key,
                      equationTransations.avgPurchase.defaultValue,
                    )}
                    layoutData={getEquationDetailsForMobile(
                      'avgPurchase',
                      chipDataMapping[selectedMobileChip] as EquationTimePeriod,
                    )}
                  />
                  <MobileChipLayout
                    layout="row"
                    layoutTitle={t(equationTransations.visitor.key, equationTransations.visitor.defaultValue)}
                    layoutData={getEquationDetailsForMobile(
                      'revVisitor',
                      chipDataMapping[selectedMobileChip] as EquationTimePeriod,
                    )}
                  />
                </>
              </ColumnFlexContainer>
            </ColumnFlexContainer>
          )}
        </>
      }
    />
  );
};
